import { useState, useEffect, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Clock,
  Sun,
  Moon,
} from "lucide-react";
import {
  ClinicDoctorListingItem,
  DoctorAppointmentSettingItem,
  PatientDetails,
  SlotTimeItem,
} from "./types";
import { AppointmentService } from "../../services/api/appointment.service";
import placeholderAvatar from "../../assets/images/default_avatar.jpg";
import bookingIllustration from "../../assets/images/select-slots.svg";
import toast from "react-hot-toast";

interface StepBookingDetailsProps {
  doctor: ClinicDoctorListingItem;
  clinicSetting: DoctorAppointmentSettingItem;
  patientDetails: PatientDetails | null;
  selectedFamilyMember: any;
  patientConcerns: string;
  onPatientConcernsChange: (value: string) => void;
  onChangeDoctor: () => void;
  onChangePatient: () => void;
  onSlotSelected: (slot: SlotTimeItem, date: string) => void;
  selectedSlot: SlotTimeItem | null;
  selectedDate: string | null;
  onDateSelected: (date: string) => void;
}

export function StepBookingDetails({
  doctor,
  clinicSetting,
  patientDetails,
  selectedFamilyMember,
  patientConcerns,
  onPatientConcernsChange,
  onChangeDoctor,
  onChangePatient,
  onSlotSelected,
  selectedSlot,
  selectedDate,
  onDateSelected,
}: StepBookingDetailsProps) {
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [slots, setSlots] = useState<SlotTimeItem[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotTab, setSlotTab] = useState<"morning" | "evening">("morning");
  const dateScrollRef = useRef<HTMLDivElement>(null);

  const isOnline = clinicSetting.clinicId.startsWith("ONL-");
  const displayPatient = selectedFamilyMember || patientDetails;

  // Fetch available dates
  useEffect(() => {
    const fetchDates = async () => {
      setLoadingDates(true);
      try {
        const response = await AppointmentService.getNextAvailableDates(
          doctor.id,
          clinicSetting.clinicId
        );
        if (response.success && response.data) {
          setAvailableDates(response.data);
          // Auto-select first date
          if (response.data.length > 0 && !selectedDate) {
            onDateSelected(response.data[0]);
          }
        } else {
          setAvailableDates([]);
        }
      } catch (err) {
        console.error("Error fetching available dates:", err);
        toast.error("Failed to load available dates");
      } finally {
        setLoadingDates(false);
      }
    };
    fetchDates();
  }, [doctor.id, clinicSetting.clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch slots when date changes
  useEffect(() => {
    if (!selectedDate) return;

    const fetchSlots = async () => {
      setLoadingSlots(true);
      try {
        const response = await AppointmentService.getDoctorSlots(
          selectedDate,
          clinicSetting.clinicId,
          doctor.id
        );

        if (response.success && response.data) {
          const now = new Date();
          const currentMinutes = now.getHours() * 60 + now.getMinutes();

          const selectedDateStr = selectedDate.split("T")[0];
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, "0");
          const day = String(now.getDate()).padStart(2, "0");
          const todayDateStr = `${year}-${month}-${day}`;

          const isToday = selectedDateStr === todayDateStr;

          // Collect all slots from response (supports flat array or nested structure)
          let rawSlots: any[] = [];

          if (Array.isArray(response.data)) {
            response.data.forEach((item: any) => {
              if (item.appointmentSetting && Array.isArray(item.appointmentSetting)) {
                item.appointmentSetting.forEach((setting: any) => {
                  if (Array.isArray(setting.slots)) {
                    rawSlots.push(...setting.slots);
                  }
                });
              } else if (item.slots && Array.isArray(item.slots)) {
                rawSlots.push(...item.slots);
              } else if (item.id && (item.startTime || item.start_time)) {
                rawSlots.push(item);
              }
            });
          } else if (response.data && typeof response.data === "object") {
            if (Array.isArray((response.data as any).slots)) {
              rawSlots.push(...(response.data as any).slots);
            }
          }

          // Map and Filter: only Available slots, and filter out past time slots for today
          const mappedSlots: SlotTimeItem[] = rawSlots
            .map((s: any) => {
              const startTimeRaw = s.startTime || s.start_time || "";
              const endTimeRaw = s.endTime || s.end_time || "";
              return {
                id: s.id,
                start_time: startTimeRaw.substring(0, 5),
                end_time: endTimeRaw.substring(0, 5),
                slot_date: s.slotDate || s.slot_date,
                status: s.status,
                type: s.type || clinicSetting.clinicName || "Clinic",
              };
            })
            .filter((slot) => {
              if (slot.status !== "Available") return false;
              if (!slot.start_time) return false;

              if (isToday) {
                const [h, m] = slot.start_time.split(":").map(Number);
                const slotMinutes = h * 60 + (m || 0);
                return slotMinutes > currentMinutes;
              }
              return true;
            });

          setSlots(mappedSlots);

          // Auto-select slot tab based on what's available
          const hasMorning = mappedSlots.some((s) => {
            const hour = parseInt(s.start_time.split(":")[0], 10);
            return hour < 12;
          });
          const hasEvening = mappedSlots.some((s) => {
            const hour = parseInt(s.start_time.split(":")[0], 10);
            return hour >= 12;
          });

          if (hasMorning) {
            setSlotTab("morning");
          } else if (hasEvening) {
            setSlotTab("evening");
          }
        } else {
          setSlots([]);
        }
      } catch (err) {
        console.error("Error fetching slots:", err);
        toast.error("Failed to load slots");
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [selectedDate, clinicSetting.clinicId, doctor.id, clinicSetting.clinicName]);


  // Split slots into morning/evening
  const morningSlots = slots.filter((s) => {
    const hour = parseInt(s.start_time.split(":")[0], 10);
    return hour < 12;
  });
  const eveningSlots = slots.filter((s) => {
    const hour = parseInt(s.start_time.split(":")[0], 10);
    return hour >= 12;
  });
  const displaySlots = slotTab === "morning" ? morningSlots : eveningSlots;

  // Format date for display
  const formatDateChip = (dateStr: string) => {
    const d = new Date(dateStr + "T00:00:00");
    const day = d.toLocaleDateString("en-US", { weekday: "short" });
    const date = d.getDate();
    return { day, date };
  };

  const formatMonthYear = (dateStr: string) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  const scrollDates = (direction: "left" | "right") => {
    if (!dateScrollRef.current) return;
    const scrollAmount = direction === "left" ? -200 : 200;
    dateScrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 h-full">
      {/* Left Side — Booking Form (3/5) */}
      <div className="lg:col-span-3 flex flex-col space-y-5">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-5">
            Booking Details
          </h3>

          {/* Doctor & Patient Cards */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 overflow-hidden mb-5">
            {/* Doctor */}
            <div className="flex items-center justify-between p-4 border-b border-emerald-200/50">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                  <img
                    src={doctor.profileImage || placeholderAvatar}
                    alt={doctor.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = placeholderAvatar;
                    }}
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Doctor</p>
                  <h4 className="font-semibold text-gray-900 text-sm">
                    Dr. {doctor.name}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {isOnline ? "Online consultation" : clinicSetting.clinicName}{" "}
                    • ₹{clinicSetting.fees}
                  </p>
                </div>
              </div>
              <button
                onClick={onChangeDoctor}
                className="px-4 py-1.5 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:bg-gray-800 transition-colors"
              >
                Change
              </button>
            </div>

            {/* Patient */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                  <img
                    src={
                      (displayPatient?.profileImage as string) ||
                      (displayPatient?.profile_image as string) ||
                      placeholderAvatar
                    }
                    alt={displayPatient?.name || "Patient"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = placeholderAvatar;
                    }}
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Patient</p>
                  <h4 className="font-semibold text-gray-900 text-sm">
                    {displayPatient?.name || "N/A"}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {selectedFamilyMember
                      ? `${selectedFamilyMember.relation}`
                      : "Account Owner"}{" "}
                    • {displayPatient?.gender || "N/A"} •{" "}
                    {displayPatient?.age
                      ? `${Math.floor(Number(displayPatient.age))} yrs`
                      : "N/A"}
                  </p>
                </div>
              </div>
              <button
                onClick={onChangePatient}
                className="px-4 py-1.5 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:bg-gray-800 transition-colors"
              >
                Change
              </button>
            </div>
          </div>

          {/* Patient Concerns */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Patient Concerns <span className="text-red-500">*</span>
            </label>
            <textarea
              value={patientConcerns}
              onChange={(e) => onPatientConcernsChange(e.target.value)}
              rows={3}
              placeholder="Enter patient symptoms, medical complaints, health issues..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm resize-none transition-colors"
            />
          </div>

          {/* Pick a Slot */}
          <div>
            <h4 className="text-base font-semibold text-gray-900 mb-3">
              Pick a slot
            </h4>

            {loadingDates ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
              </div>
            ) : availableDates.length > 0 ? (
              <>
                {/* Month Label */}
                <p className="text-sm text-gray-500 mb-2">
                  {formatMonthYear(selectedDate || availableDates[0])}
                </p>

                {/* Date Chips */}
                <div className="relative mb-4">
                  <button
                    onClick={() => scrollDates("left")}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 rounded-full p-1 shadow border border-gray-200 hover:bg-gray-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div
                    ref={dateScrollRef}
                    className="flex gap-2 overflow-x-auto px-8 py-1 scrollbar-hide"
                    style={{ scrollbarWidth: "none" }}
                  >
                    {availableDates.map((dateStr) => {
                      const { day, date } = formatDateChip(dateStr);
                      const isSelected = dateStr === selectedDate;
                      return (
                        <button
                          key={dateStr}
                          onClick={() => onDateSelected(dateStr)}
                          className={`flex-shrink-0 px-4 py-2 rounded-full border text-xs font-medium transition-all ${
                            isSelected
                              ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                              : "bg-white text-gray-700 border-gray-300 hover:border-emerald-400"
                          }`}
                        >
                          {day}, {date}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => scrollDates("right")}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 rounded-full p-1 shadow border border-gray-200 hover:bg-gray-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Slot Tabs */}
                <div className="flex items-center gap-3 mb-4">
                  <button
                    onClick={() => setSlotTab("morning")}
                    className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-all border ${
                      slotTab === "morning"
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "text-gray-500 border-gray-200 hover:border-emerald-300"
                    }`}
                  >
                    <Sun className="w-3.5 h-3.5" />
                    Morning Slots ({morningSlots.length})
                  </button>
                  <button
                    onClick={() => setSlotTab("evening")}
                    className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-all border ${
                      slotTab === "evening"
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "text-gray-500 border-gray-200 hover:border-emerald-300"
                    }`}
                  >
                    <Moon className="w-3.5 h-3.5" />
                    Evening Slots ({eveningSlots.length})
                  </button>
                </div>

                {/* Slots Grid */}
                {loadingSlots ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                  </div>
                ) : displaySlots.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {displaySlots.map((slot) => {
                      const isSelected = selectedSlot?.id === slot.id;
                      return (
                        <button
                          key={slot.id}
                          onClick={() => {
                            if (selectedDate) onSlotSelected(slot, selectedDate);
                          }}
                          className={`px-3 py-2.5 rounded-xl text-xs font-medium transition-all border flex items-center justify-center gap-1 ${
                            isSelected
                              ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                              : "bg-white text-gray-700 border-gray-300 hover:border-emerald-400 hover:bg-emerald-50"
                          }`}
                        >
                          <Clock className="w-3 h-3" />
                          {slot.start_time} - {slot.end_time}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-6">
                    No {slotTab} slots available for this date.
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500 text-center py-6">
                No available dates found for this doctor.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Right Side — Illustration (2/5) */}
      <div className="lg:col-span-2 hidden lg:flex flex-col items-center justify-center">
        <img
          src={bookingIllustration}
          alt="Booking"
          className="w-auto h-56 opacity-80 mb-4"
        />
        <p className="text-center text-sm text-gray-500 max-w-[250px]">
          Choose a slot, confirm payment, and the appointment is booked.
        </p>
      </div>
    </div>
  );
}
